import { alpha, Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import MUIButtonBase, { ButtonBaseProps as MUIButtonBaseProps } from '@mui/material/ButtonBase';
import { DEFAULT_BORDER_RADIUS } from 'theme/constants';
import { palette } from 'theme/palette';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import React from 'react';
import { pxToRem } from 'src/common/utils/fonts';

const buttonBase: SxProps = {
  borderRadius: DEFAULT_BORDER_RADIUS,
  px: 16,
  py: 10,
  typography: 'h4',
  '&:has(.text + .icon)': {
    pr: 8,
  },
  '&:has(.icon + .text)': {
    pl: 8,
  },
};

const mediumButton: SxProps = {
  py: 8,
};

const smallButton: SxProps = {
  py: 7,
};

const buttonPrimarySX: SxProps<Theme> = {
  ...buttonBase,
  backgroundColor: ({ palette }) => palette.grey[600],
  color: ({ palette }) => palette.text.white,
  '&:hover, &:active': {
    backgroundColor: ({ palette }) => palette.grey[500],
    color: ({ palette }) => palette.text.white,
  },
  '&:disabled': {
    color: palette.text.tertiary,
    backgroundColor: palette.border,
  },
};

const buttonSecondarySX: SxProps<Theme> = {
  ...buttonBase,
  backgroundColor: ({ palette }) => palette.background.paper,
  color: ({ palette }) => palette.text.secondary,
  boxShadow: pxToRem(4),
  '&:hover': {
    boxShadow: 0,
    backgroundColor: ({ palette }) => palette.grey[200],
    color: ({ palette }) => palette.text.primary,
  },
  '&:active': {
    boxShadow: 0,
    backgroundColor: ({ palette }) => palette.grey[300],
    color: ({ palette }) => palette.text.secondary,
  },
  '&:disabled': {
    color: palette.text.tertiary,
    backgroundColor: palette.border,
  },
};

const buttonSecondaryActiveSX: SxProps<Theme> = {
  ...buttonBase,
  backgroundColor: ({ palette }) => palette.grey[200],
  color: ({ palette }) => palette.text.secondary,
};

const buttonTertiarySX: SxProps<Theme> = {
  ...buttonBase,
  backgroundColor: ({ palette }) => alpha(palette.background.paper, 0),
  color: ({ palette }) => palette.text.secondary,
  '&:hover, &:active': {
    boxShadow: 0,
    backgroundColor: ({ palette }) => palette.grey[200],
  },
  '&:disabled': {
    color: palette.text.tertiary,
  },
};

const buttonDangerSX: SxProps<Theme> = {
  ...buttonBase,
  backgroundColor: ({ palette }) => palette.red[600],
  color: ({ palette }) => palette.text.white,
  '&:hover, &:active': {
    backgroundColor: ({ palette }) => palette.red[500],
    color: ({ palette }) => palette.text.white,
  },
  '&:disabled': {
    color: palette.text.tertiary,
    backgroundColor: palette.border,
  },
};

const buttonStyles = {
  primary: buttonPrimarySX,
  secondary: buttonSecondarySX,
  secondaryActive: buttonSecondaryActiveSX,
  tertiary: buttonTertiarySX,
  danger: buttonDangerSX,
};

export type ButtonProps = {
  id?: string;
  variant: keyof typeof buttonStyles;
  children: string | React.ReactNode;
  onClick?: MUIButtonBaseProps['onClick'];
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactElement;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps;
};

const iconBox: SxProps<Theme> = {
  width: '1.5rem',
  height: '1.5rem',
};

export const Button: React.FC<ButtonProps> = ({
  variant,
  children,
  iconStart,
  iconEnd,
  size = 'large',
  sx,
  ...otherProps
}) => {
  const buttonSx = {
    ...buttonStyles[variant],
    ...(size === 'small' ? smallButton : {}),
    ...(size === 'medium' ? mediumButton : {}),
    ...sx,
  };

  return (
    <MUIButtonBase sx={buttonSx} disableTouchRipple disableRipple {...otherProps}>
      <Stack spacing={1.5} direction={'row'} alignItems="center">
        {iconStart && (
          <Box sx={iconBox} className="icon">
            {iconStart}
          </Box>
        )}
        <Box className="text">{children}</Box>
        {iconEnd && (
          <Box sx={iconBox} className="icon">
            {iconEnd}
          </Box>
        )}
      </Stack>
    </MUIButtonBase>
  );
};
